import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ProSideBar from '../Components/NavBars/ProSideBar';
import { useAuthState } from 'react-firebase-hooks/auth';
import Firebase from "../Components/Firebase/Firebase";
import { ref, getDatabase, query, orderByChild, equalTo } from 'firebase/database';
import { getAuth, signOut } from 'firebase/auth';
import { Avatar, Toolbar, Menu, MenuItem, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import logo from "../images/logo.png";
import Navigation from '../Components/Navigation';
import { Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import { getFunctionsApiBaseUrl } from "../Components/utils/functionsApi";

const drawerWidth = 250;

const openedMixin = (theme) => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = (theme) => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(7)} + 1px)`,
	},
});

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(['width', 'margin'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	variants: [
		{
			props: ({ open }) => open,
			style: {
				marginLeft: drawerWidth,
				transition: theme.transitions.create(['width', 'margin'], {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.enteringScreen,
				}),
			},
		},
	],
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
	({ theme }) => ({
		width: drawerWidth,
		height: '100dvh',
		flexShrink: 0,
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
		'& .MuiDrawer-paper': {
			border: 'none',
			borderRadius: 15,
			margin: '10px',
		},
		variants: [
			{
				props: ({ open }) => open,
				style: {
					...openedMixin(theme),
					'& .MuiDrawer-paper': {
						...openedMixin(theme),
						border: 'none',
						borderRadius: 15,
						margin: '10px',
					},
				},
			},
			{
				props: ({ open }) => !open,
				style: {
					...closedMixin(theme),
					'& .MuiDrawer-paper': {
						...closedMixin(theme),
						border: 'none',
						borderRadius: 15,
						margin: '10px',
					},
				},
			},
		],
	})
);

export default function CoreApp() {
	const theme = useTheme();
	const [open, setOpen] = React.useState(false);
	const database = getDatabase(Firebase);
	const auth = getAuth(Firebase);
	const [checked, setChecked] = React.useState(true);
	const [userName, setUserName] = React.useState(null);
	const [user, loading, error] = useAuthState(auth);
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [openMenu, setOpenMenu] = React.useState(false);
	const [isLoggingOut, setIsLoggingOut] = React.useState(false);
	const location = useLocation();

	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
		setOpenMenu(true);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setOpenMenu(false);
	};

	const handleLogout = async () => {
		if (isLoggingOut) return;
		setIsLoggingOut(true);
		setOpenMenu(false);

		try {
			const user = auth.currentUser;

			if (user) {
				const idToken = await user.getIdToken(true);
				const apiBaseUrl = getFunctionsApiBaseUrl();
				const res = await fetch(`${apiBaseUrl}/logout`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${idToken}`,
					},
					body: JSON.stringify({}),
				});
				if (!res.ok) {
					console.error("Logout API returned non-200:", res.status);
				}
			}
		} catch (err) {
			console.error("Logout API error", err);
		}

		try {
			await signOut(auth);
			window.location.replace("/login");
		} catch (err) {
			console.error("Local signOut error", err);
			alert("Oops, an error occurred while logging out.");
		} finally {
			setIsLoggingOut(false);
		}
	};

	React.useEffect(() => {
		if (user) {
			const usersRef = ref(database, "talmidsdb/users/");
			const q = query(usersRef, orderByChild("email"), equalTo(user.email));
			setUserName(user?.displayName);
		} else {
			setUserName(null);
		}
	}, [checked, error, user, database]);

	const handleDrawerOpen = () => {
		setOpen(!open);
	};

	if (loading) {
		return (
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100vh'
			}}>
				<CircularProgress size={40} />
			</div>
		);
	}

	const isAuthRoute =
		location.pathname === '/login' ||
		location.pathname === '/forgot';

	return (
		<Box sx={{ display: 'flex', zoom: '90%' }}>
			<CssBaseline />
			{isLoggingOut && (
				<Box
					sx={{
						position: "fixed",
						inset: 0,
						zIndex: 2000,
						backgroundColor: "rgba(255,255,255,0.35)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<CircularProgress size={44} />
				</Box>
			)}

			{!isAuthRoute && (
				<AppBar
					position="fixed"
					open={open}
					style={{
						backgroundColor: "#51195D",
						zIndex: 1201,
					}}
				>
					<Toolbar>
						<IconButton
							color="inherit"
							aria-label="open drawer"
							onClick={handleDrawerOpen}
							edge="start"
							sx={[
								{
									marginRight: 5,
								},
							]}
						>
							{open ? <ChevronLeftIcon /> : <MenuIcon />}
						</IconButton>
						<Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
							<Box display="flex">
								<div>
									<img src={logo} className="w-[100px]" alt="aligment" />
								</div>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								<Typography style={{ color: "white" }}>{userName}</Typography>
								<Avatar
									onClick={isLoggingOut ? undefined : handleMenuOpen}
									className="cursor-pointer"
									sx={{ width: 24, height: 24 }}
								/>
								<Menu
									anchorEl={anchorEl}
									open={openMenu}
									onClose={handleMenuClose}
									MenuListProps={{
										'aria-labelledby': 'basic-button',
									}}
								>
									<MenuItem onClick={handleLogout} disabled={isLoggingOut}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											{isLoggingOut ? <CircularProgress size={14} /> : null}
											{isLoggingOut ? "Logging out..." : "Log Out"}
										</Box>
									</MenuItem>
								</Menu>
							</Box>
						</Box>
					</Toolbar>
				</AppBar>
			)}

			{!isAuthRoute && (
				<Drawer
					variant="permanent"
					open={open}
					borderRadius={20}
					sx={{
						backgroundColor: 'transparent',
						boxShadow: 'none',
					}}
				>
					<Toolbar />
					<Box
						style={{
							borderRadius: 15,
							boxShadow: theme.shadows[3],
							margin: '1px',
							height: '100dvh',
						}}
					>
						<ProSideBar checked={open} />
					</Box>
				</Drawer>
			)}

			<Box
				component="main"
				sx={{
					flexGrow: 1,
					p: 3,
					marginTop: isAuthRoute ? 0 : 2,
				}}
			>
				<Navigation user={user} />
			</Box>
		</Box>
	);
}
